import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import MapComponent from '../components/Map';
import { Marker } from 'react-map-gl/maplibre';
import { getCategories } from '../hooks/GetCategories';
import { LocalPoiCategory } from '../interface/LocalPoiCategory';
import { LocalPoi } from '../interface/LocalPoi';
import { LocalPoiFile } from '../interface/LocalPoiFile';
import Navbar from '../components/Navbar';
import { deletePOI } from '../hooks/DeleteLocalPoi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { setPosition, triggerPoiReload } from '../redux/mapSlice';
import { useTranslation } from 'react-i18next';
import { postPoi } from '../hooks/PostLocalPoi';
import { postCategory } from '../hooks/PostCategory';
import FileUpload from '../components/FileUpload';
import CategoryForm from '../components/CategoryForm';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import { getMunicipality } from '../hooks/GetMunicipality';
import { setMunicipality } from '../redux/municipialitySlice';
import { addMessage } from '../redux/infoMessageSlice';

export default function PoiFormPage() {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const auth: string = useAuthHeader() || '';
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const poi: LocalPoi = location.state?.poi;
  const [isLoadingMunicipality, setIsLoadingMunicipality] =
    useState<boolean>(true);
  const [title, setTitle] = useState<string>(poi?.title || '');
  const [titleError, setTitleError] = useState<boolean>(false);
  const [categoryError, setCategoryError] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(
    poi?.description || ''
  );
  const isMobile = useMediaQuery('(max-width:600px)');
  const [modalOpen, setModalOpen] = useState(false);
  const [category, setCategory] = useState<string>(poi?.category.name || '');
  const [cancelModelOpen, setCancelModalOpen] = useState(false);
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [categories, setCategories] = useState<LocalPoiCategory[]>([]);
  const longitude = useSelector((state: RootState) => state.map.longitude);
  const latitude = useSelector((state: RootState) => state.map.latitude);
  const municipality = useSelector(
    (state: RootState) => state.municipality.selectedMunicipality
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<
    { file: File; name: string; id?: number }[]
  >(
    poi?.files?.map((file: LocalPoiFile) => ({
      file: new File([], file.name),
      name: file.name,
      id: file.id,
    })) || []
  );

  const fetchCategories = async () => {
    const result = await getCategories(auth);
    if (Array.isArray(result)) {
      const localCategories = result.filter(
        (category: LocalPoiCategory) => category.local
      );
      setCategories(localCategories);
      if (localCategories.length > 0 && category === '') {
        setCategory(localCategories[localCategories.length - 1].name);
      }
    } else {
      dispatch(addMessage({ message: 'error.failedToFetch', error: result }));
    }
  };

  useEffect(() => {
    let fetchedMunicipality;
    if (!municipality && auth) {
      setIsLoadingMunicipality(true);
      fetchedMunicipality = getMunicipality(auth, dispatch);
      fetchedMunicipality
        .then((fetchedMunicipality) => {
          if (fetchedMunicipality !== null) {
            dispatch(setMunicipality(fetchedMunicipality));
          }
        })
        .finally(() => {
          setIsLoadingMunicipality(false);
        });
    } else {
      setIsLoadingMunicipality(false);
    }
  }, [municipality, auth, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      window.location.reload();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCategories();
  }, [auth]);

  const handleFileChange = (index: number, file: File) => {
    const updatedFiles = [...files];
    updatedFiles[index] = {
      ...updatedFiles[index],
      file,
      id: undefined,
    };
    setFiles(updatedFiles);
  };

  const handleAddFile = () => {
    setFiles([...files, { file: new File([], ''), id: undefined, name: '' }]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles([...files.filter((_, i) => i !== index)]);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (title === '') {
      setTitleError(true);
      if (category === '') {
        setCategoryError(true);
      }
      return;
    }
    if (category === '') {
      setTitleError(false);
      setCategoryError(true);
      return;
    }
    setLoading(true);
    try {
      const result = await postPoi(
        auth,
        poi,
        title,
        description,
        category,
        latitude,
        longitude,
        files
      );
      if (result === 'Local POI saved successfully') {
        navigate('/');
        dispatch(
          addMessage({
            message: 'poiForm.additionSuccessful',
            type: 'success',
          })
        );
      } else {
        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: result,
          })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).message,
        })
      );
    } finally {
      setLoading(false);
      setTitleError(false);
      setCategoryError(false);
    }
  };

  const handleCategoryFormSubmit = async (
    categoryName: string,
    icon: string
  ) => {
    try {
      await postCategory(auth, categoryName, icon);

      const result = await getCategories(auth);

      if (Array.isArray(result)) {
        const localCategories = result.filter(
          (category: LocalPoiCategory) => category.local
        );

        setCategories(localCategories);

        const newCategory = localCategories.find(
          (category: LocalPoiCategory) => category.name === categoryName
        );

        if (newCategory) {
          const savedCategories = localStorage.getItem('visibleCategories');
          const visibleCategories: number[] = savedCategories
            ? JSON.parse(savedCategories)
            : [];

          if (!visibleCategories.includes(newCategory.id)) {
            const updatedVisibleCategories = [
              ...visibleCategories,
              newCategory.id,
            ];

            localStorage.setItem(
              'visibleCategories',
              JSON.stringify(updatedVisibleCategories)
            );
          }

          setCategory(newCategory.name);
        }
      } else {
        dispatch(addMessage({ message: 'error.failedToFetch', error: result }));
      }

      setCategoryFormOpen(false);
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const handleOpenCancelModal = () => {
    setCancelModalOpen(true);
  };
  const handleCloseCancelModal = () => {
    setCancelModalOpen(false);
  };
  const handleConfirmCancel = () => {
    setCancelModalOpen(false);
    navigate('/');
  };

  const deletePoi = async () => {
    const result = await deletePOI(auth, poi.id);
    if (result === 'Local POI removed successfully') {
      dispatch(triggerPoiReload());
      location.state.poi = undefined;
      navigate('/');
      dispatch(
        addMessage({
          message: 'poiForm.deletionSuccessful',
          type: 'success',
        })
      );
    } else {
      console.error('Failed to delete POI:', result);
    }
  };

  const handleMarkerDragEnd = (event: {
    lngLat: { lng: number; lat: number };
  }) => {
    const { lng, lat } = event.lngLat;
    dispatch(setPosition({ longitude: lng, latitude: lat }));
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Navbar />
      {isLoadingMunicipality ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : municipality ? (
        <>
          <CategoryForm
            open={categoryFormOpen}
            onClose={() => setCategoryFormOpen(false)}
            onSubmit={handleCategoryFormSubmit}
            loading={loading}
          />
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Typography component="h1" variant="h5">
              {t('poiForm.title')}
            </Typography>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: isMobile ? 'center' : 'start',
              }}
            >
              <Box
                sx={{
                  mr: isMobile ? '0' : '24px',
                  ml: '0px',
                  width: isMobile ? '90%' : '25%',
                  height: isMobile ? 'auto' : '80%',
                }}
              >
                <TextField
                  fullWidth
                  label={t('poiForm.name')}
                  name="title"
                  autoComplete="title"
                  autoFocus
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={{ mb: 0.5 }}
                />
                {titleError && (
                  <Typography sx={{ color: 'red', mb: 2 }}>
                    {t('poiForm.missingName')}
                  </Typography>
                )}

                <Select
                  fullWidth
                  name="category"
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  displayEmpty
                  sx={{ mb: 0.5 }}
                >
                  <MenuItem value="" disabled>
                    {t('poiForm.selectCategory')}
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {categoryError && (
                  <Typography sx={{ color: 'red', mb: 0.5 }}>
                    {t('poiForm.missingCategory')}
                  </Typography>
                )}
                <Button
                  sx={{
                    textTransform: 'none',
                    padding: 0,
                    fontSize: 'inherit',
                    fontWeight: 'normal',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  onClick={() => setCategoryFormOpen(true)}
                >
                  {t('poiForm.addNewCategory')}
                </Button>
                <TextField
                  fullWidth
                  label={t('poiForm.description')}
                  name="description"
                  autoComplete="description"
                  type="text"
                  id="description"
                  value={description}
                  multiline
                  rows={4}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <FileUpload
                  files={files}
                  handleFileChange={handleFileChange}
                  handleRemoveFile={handleRemoveFile}
                  handleAddFile={handleAddFile}
                />
              </Box>
              <Box
                sx={{
                  width: isMobile ? '90%' : '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    height: isMobile ? 'auto' : '80%',
                    width: '100%',
                  }}
                >
                  <div
                    style={{ width: '100%', height: isMobile ? '40vh' : '90%' }}
                  >
                    <MapComponent
                      onBoundingBoxChange={() => {
                        return;
                      }}
                    >
                      <Marker
                        longitude={longitude}
                        latitude={latitude}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                      />
                    </MapComponent>
                  </div>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'right',
                      alignItems: 'end',
                      mt: '12px',
                      mb: '12px',
                      height: isMobile ? 'auto' : '10%',
                    }}
                  >
                    <Button
                      id={'cancel-button'}
                      type="button"
                      variant="contained"
                      color="error"
                      sx={{ marginRight: 2 }}
                      onClick={handleOpenCancelModal}
                    >
                      {t('poiForm.cancel')}
                    </Button>

                    {poi && (
                      <Button
                        id={'delete-button'}
                        type="button"
                        variant="contained"
                        color="error"
                        sx={{ marginRight: 2 }}
                        onClick={handleOpenModal}
                      >
                        {t('poiForm.delete')}
                      </Button>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      sx={{ marginRight: 2 }}
                    >
                      {t('poiForm.save')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Modal open={modalOpen} onClose={handleCloseModal}>
            <Box
              id={'delete-poi-confirmation-window'}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? '75%' : 400,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
              }}
            >
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                {t('poiForm.deletePoi')}
              </Typography>
              <Typography sx={{ mb: 4 }}>
                {t('poiForm.confirmDelete')}
              </Typography>
              <Box
                id={'cancel-button'}
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
              >
                <Button variant="contained" onClick={handleCloseModal}>
                  {t('poiForm.cancel')}
                </Button>
                <Button variant="contained" color="error" onClick={deletePoi}>
                  {t('poiForm.delete')}
                </Button>
              </Box>
            </Box>
          </Modal>

          <Modal open={cancelModelOpen} onClose={handleCloseCancelModal}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? '75%' : 400,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
              }}
            >
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                {t('poiForm.cancelAction')}
              </Typography>
              <Typography sx={{ mb: 4 }}>
                {t('poiForm.confirmCancel')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="contained" onClick={handleCloseCancelModal}>
                  {t('poiForm.no')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleConfirmCancel}
                >
                  {t('poiForm.yes')}
                </Button>
              </Box>
            </Box>
          </Modal>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: '20vh',
          }}
        >
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            {t('poiForm.missingMunicipality')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              navigate('/municipality');
            }}
          >
            {t('poiForm.toMunicipality')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
